import { defer } from 'react-router-dom'
import { isAppStateReady } from 'src/state/store'
import { getActiveCompanySettings, isXeroPayrollSystem, logger } from '$utils'
import useWorkforceStore from '../../store'
import { AuthModule, PayrollSystem } from '$constants'

export default async function loader({ params }) {
  logger.debug(
    '[loader] About to await AppState being ready in EditStaffMember loader'
  )
  await isAppStateReady()
  logger.debug(
    '[loader] AppState should now be ready in EditStaffMember loader'
  )

  // All data fetching (including the third-party connection check) is done
  // inside the deferred promise. This lets React Router commit the navigation
  // immediately so the URL updates and the Suspense fallback (loading bar)
  // appears without waiting on network calls in the loader.
  return defer({
    data: loadEditStaffMemberData(params.staffMemberId),
  })
}

async function loadEditStaffMemberData(
  staffMemberId: string
): Promise<boolean> {
  const { hasManagerPermission, payrollSystemPreference } =
    getActiveCompanySettings()

  const isXeroPayroll = isXeroPayrollSystem(payrollSystemPreference)

  //We need to check the connectivity if it has XeroAu or XeroNz because we also need to get
  //the third party employee information if there is a third party connection
  const isThirdPartyPayrollSystemConnected =
    isXeroPayroll &&
    (await useWorkforceStore
      .getState()
      .checkThirdPartyConnectionStatus(AuthModule.Time))

  const getCompanyDefaultWorkingHours = useWorkforceStore
    .getState()
    .getCompanyDefaultWorkingHours()

  const getStaffMember = staffMemberId
    ? useWorkforceStore
        .getState()
        .getStaffMember(staffMemberId)
        .then(async (result) => {
          if (!isXeroPayroll) return

          // The api resolves a link through the venue that owns it. So a linked staff
          // member still has an employee and calendars to load, even if this venue is
          // not connected.
          const isLinkedToPayrollEmployee = Boolean(
            result?.thirdPartyEmployeeId
          )

          if (!isLinkedToPayrollEmployee && !isThirdPartyPayrollSystemConnected)
            return

          if (isLinkedToPayrollEmployee) {
            // Read by staff member so the call goes through the venue that owns the
            // link, which is not the active one for staff on a single payroll company
            await useWorkforceStore
              .getState()
              .getLinkedPayrollEmployee(
                staffMemberId,
                payrollSystemPreference as PayrollSystem
              )
          }

          // Calendars come from the venue that owns the link. Chained rather than
          // parallel because that venue is only known once the staff member is loaded
          await useWorkforceStore
            .getState()
            .getCalendars(
              payrollSystemPreference as PayrollSystem,
              result?.thirdPartyEmployeeVenueId
            )
        })
    : null

  const getPosStaffMembers = useWorkforceStore.getState().getPosStaff(false)

  const getStaffRoles = useWorkforceStore.getState().getStaffRoles(true)

  const activeStaffMembers = useWorkforceStore.getState().activeStaffMembers
  const getStaffMembers =
    activeStaffMembers.length == 0
      ? useWorkforceStore.getState().getStaffMembers(false, false)
      : activeStaffMembers

  const getPayClassifications = useWorkforceStore
    .getState()
    .getPayClassifications()

  //We only want to do this for XeroAu as earnings rates are not available for XeroNz
  const getEarningsRates =
    isThirdPartyPayrollSystemConnected &&
    payrollSystemPreference === PayrollSystem.XeroAu
      ? useWorkforceStore
          .getState()
          .getEarningsRates(payrollSystemPreference as PayrollSystem)
      : []

  //- This manager is checked here and not in the service because it needs to be called from
  //the RequestEmployeeInformation, which is a public page, and it also needs to be called
  //from the PayrollTab in the EditStaffMember page which is only accessible for managers
  //- We only want to do this for XeroAu as super funds are not available for XeroNz
  const getPayrollSuperFunds =
    isThirdPartyPayrollSystemConnected &&
    hasManagerPermission &&
    payrollSystemPreference === PayrollSystem.XeroAu
      ? useWorkforceStore
          .getState()
          .getPayrollSuperFunds(payrollSystemPreference as PayrollSystem)
      : []

  // Only Xero AU needs details of possible super funds.
  const getSuperFunds =
    isThirdPartyPayrollSystemConnected &&
    payrollSystemPreference === PayrollSystem.XeroAu
      ? useWorkforceStore.getState().getSuperFunds()
      : []

  const getUnavailabilitiesByStaffMemberId = useWorkforceStore
    .getState()
    .getUnavailabilitiesByStaffMemberId(staffMemberId)

  const getLeaveTypesByStaffMemberId = useWorkforceStore
    .getState()
    .getLeaveTypesByStaffMemberId(staffMemberId)

  const getLeaveRequestsByStaffMemberId = useWorkforceStore
    .getState()
    .getLeaveRequestsByStaffMemberId(staffMemberId)

  try {
    await Promise.all([
      getStaffMember,
      getPosStaffMembers,
      getStaffRoles,
      getStaffMembers,
      getPayClassifications,
      getEarningsRates,
      getPayrollSuperFunds,
      getSuperFunds,
      getUnavailabilitiesByStaffMemberId,
      getLeaveTypesByStaffMemberId,
      getLeaveRequestsByStaffMemberId,
      getCompanyDefaultWorkingHours,
    ])
    return true
  } catch (error) {
    logger.error('Edit Staff Member Promise.all error', error)
    return false
  }
}
